import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '$lib/hooks/useTheme';
import { RootLayout } from '$lib/routes/RootLayout';
import { UnitsPage } from '$lib/routes/UnitsPage';
import { DevComponentsPage } from '$lib/routes/DevComponentsPage';
import { Placeholder } from '$lib/routes/Placeholder';
import { SavedSearchesPage } from '$lib/routes/SavedSearchesPage';
import { AddressSearchPage } from '$lib/routes/AddressSearchPage';
import { AdminDashboardPage } from '$lib/routes/AdminDashboardPage';
import { AdminScopePage } from '$lib/routes/AdminScopePage';
import { AdminOutliersPage } from '$lib/routes/AdminOutliersPage';
import { EstimatePage } from '$lib/routes/EstimatePage';
import { ListingDetailPage } from '$lib/routes/ListingDetailPage';
import { ReportPage } from '$lib/routes/ReportPage';

// Detail page is route-level lazy so recharts and the detail components are
// pulled in only when an analyst opens a unit. Keeps the table view small.
const UnitDetailPage = lazy(() =>
  import('$lib/routes/UnitDetailPage').then((m) => ({ default: m.UnitDetailPage }))
);

// Map page is also lazy — react-leaflet + leaflet's CSS together are ~50KB
// and only load when the operator hits /map. The chunk also imports the
// browser-only `window` directly via leaflet, so deferring it sidesteps any
// SSR concern if we ever pre-render the shell.
const MapPage = lazy(() =>
  import('$lib/routes/MapPage').then((m) => ({ default: m.MapPage }))
);

// Run detail is also lazy — it pulls the pipeline visual + counters + error
// log only when an operator opens a specific run, keeping the main admin
// dashboard's bundle small.
const AdminRunDetailPage = lazy(() =>
  import('$lib/routes/AdminRunDetailPage').then((m) => ({ default: m.AdminRunDetailPage }))
);

function RouteSuspense({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Suspense
      fallback={
        <div className="flex flex-1 items-center justify-center p-12 text-xs text-fg-muted" role="status">
          {t('common.loading')}
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false
    }
  }
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Navigate to="/units" replace /> },
      { path: 'units', element: <UnitsPage /> },
      { path: 'listings/:listingId', element: <ListingDetailPage /> },
      { path: 'search', element: <AddressSearchPage /> },
      { path: 'estimate', element: <EstimatePage /> },
      {
        path: 'units/:unitId',
        element: (
          <RouteSuspense>
            <UnitDetailPage />
          </RouteSuspense>
        )
      },
      {
        path: 'map',
        element: (
          <RouteSuspense>
            <MapPage />
          </RouteSuspense>
        )
      },
      {
        path: 'signals',
        element: (
          <Placeholder
            titleKey="placeholder.signals.title"
            milestone="M5"
            descriptionKey="placeholder.signals.description"
          />
        )
      },
      { path: 'saved', element: <SavedSearchesPage /> },
      { path: 'report', element: <ReportPage /> },
      { path: 'admin', element: <AdminDashboardPage /> },
      { path: 'admin/scope', element: <AdminScopePage /> },
      { path: 'admin/outliers', element: <AdminOutliersPage /> },
      {
        path: 'admin/runs/:runId',
        element: (
          <RouteSuspense>
            <AdminRunDetailPage />
          </RouteSuspense>
        )
      },
      { path: 'dev/components', element: <DevComponentsPage /> },
      { path: '*', element: <Navigate to="/units" replace /> }
    ]
  }
]);

export function App() {
  // Bind theme on first paint so router children can render under the right token set.
  const { theme } = useTheme();
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
